import { useState } from 'react';
import { cta, pricing } from '../content';
import { Arrow, Button, SmartLink, Swipe, cx, usePageMeta, useReveal } from '../ui';
import { CtaBand, Faq, KitStyles, Section, TwoTone, breadcrumbJsonLd, useJsonLd } from './platform/kit';
import { PRICE_LIST_EFFECTIVE, PRICE_LIST_VERSION, meters, other, partner, plans, pricingFaqs, type Cur } from './pricing/data';

const connectPlan = pricing.plans.find((p) => p.name === 'Connect')!;
const enterprisePlan = pricing.plans.find((p) => p.name === 'Enterprise')!;

function CurrencyToggle({ cur, set }: { cur: Cur; set: (c: Cur) => void }) {
  return (
    <div className="inline-flex rounded-full bg-bm-alt p-1 shadow-[inset_0_0_0_1px_#E4E3F0]" role="group" aria-label="Currency">
      {(['GBP', 'EUR'] as Cur[]).map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={cur === c}
          onClick={() => set(c)}
          className={cx('min-h-[36px] rounded-full px-4 text-[14px] font-semibold transition-colors', cur === c ? 'bg-white text-bm-indigo shadow-[0_1px_3px_rgba(24,19,67,.12)]' : 'text-bm-muted hover:text-bm-indigo')}
        >
          {c === 'GBP' ? '£ GBP' : '€ EUR'}
        </button>
      ))}
    </div>
  );
}

function Check() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="mt-[3px] flex-none">
      <circle cx="9" cy="9" r="9" fill="#E8F5EC" />
      <path d="M5.2 9.3l2.4 2.3 5.2-5.2" fill="none" stroke="#2F9E58" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Plans({ cur }: { cur: Cur }) {
  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr] lg:items-stretch">
        {/* Scale: the recommendation */}
        <div className="frame h-full" data-reveal>
          <div className="flex h-full flex-col p-7 sm:p-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-[28px] font-extrabold tracking-[-0.03em] text-bm-indigo">{plans.scale.name}</h2>
              <span className="rounded-full bg-bm-indigo px-3 py-1 text-[12.5px] font-semibold text-white">Recommended</span>
            </div>
            <p className="mt-1 text-[16px] text-bm-muted">Your whole range.</p>
            <div className="mt-7 flex items-baseline gap-2">
              <span className="pr-price">{plans.scale[cur]}</span>
              <span className="text-[15px] text-bm-muted">a month</span>
            </div>
            <div className="mt-1 text-[14px] text-bm-muted">12-month term, paid monthly. Excludes VAT.</div>
            <ul className="mt-8 grid gap-3 border-t border-bm-line pt-7 sm:grid-cols-2">
              {['25,000 tags a year', '1,000 AI resolutions a month', '1,500 AI generations a month', '2,000 AI thinking units a month', 'Dealer add-on available'].map((x) => (
                <li key={x} className="flex gap-2.5 text-[15.5px] text-bm-ink">
                  <Check />
                  {x}
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-9">
              <Button href={`${cta.primaryHref}?plan=scale`} className="w-full sm:w-auto">
                {cta.primary} <Arrow />
              </Button>
            </div>
          </div>
        </div>

        {/* Connect: the pilot */}
        <div className="flex h-full flex-col rounded-[22px] bg-white p-7 shadow-[0_0_0_1px_#E4E3F0] sm:p-10" data-reveal style={{ ['--reveal-delay' as string]: '0.08s' }}>
          <h2 className="text-[28px] font-extrabold tracking-[-0.03em] text-bm-indigo">{plans.connect.name}</h2>
          <p className="mt-1 text-[16px] text-bm-muted">{connectPlan.for}.</p>
          <div className="mt-7 flex items-baseline gap-2">
            <span className="pr-price pr-price-sm">{plans.connect[cur]}</span>
            <span className="text-[15px] text-bm-muted">a month</span>
          </div>
          <div className="mt-1 text-[14px] text-bm-muted">Month to month. Excludes VAT.</div>
          <ul className="mt-8 space-y-3 border-t border-bm-line pt-7">
            {['5,000 tags a year', '250 AI resolutions a month', '300 AI generations a month', '500 AI thinking units a month'].map((x) => (
              <li key={x} className="flex gap-2.5 text-[15.5px] text-bm-ink">
                <Check />
                {x}
              </li>
            ))}
          </ul>
          <div className="mt-auto pt-9">
            <SmartLink href={`${cta.primaryHref}?plan=connect`} className="pk-textlink">
              Talk to us about a pilot <Arrow />
            </SmartLink>
          </div>
        </div>
      </div>

      {/* Enterprise: a quiet line */}
      <div className="mt-6 grid items-center gap-3 rounded-[18px] border border-bm-line px-6 py-5 text-left sm:grid-cols-[1fr_auto] sm:px-10" data-reveal>
        <div>
          <div className="text-[17px] font-bold text-bm-indigo">{plans.enterprise.name}</div>
          <div className="text-[15.5px] text-bm-muted">{enterprisePlan.for}. Allowances and rates by quote, set out in your Order Form.</div>
        </div>
        <SmartLink href={`${cta.primaryHref}?plan=enterprise`} className="pk-textlink flex-none">
          Ask for a quote <Arrow />
        </SmartLink>
      </div>
    </>
  );
}

function Dealers({ cur }: { cur: Cur }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-16">
      <div data-reveal>
        <h2 className="t-h2">
          Dealers, priced by branch. <span className="pk-muted">Every dealer user included.</span>
        </h2>
        <p className="mt-6 max-w-lg text-[16.5px] text-bm-muted">
          The Partner (Dealer) add-on shows dealers, installers and service agents the units they sell and look after. Requires Scale, on a 12-month term paid monthly.
        </p>
        <SmartLink href="/platform/dealers" className="pk-textlink mt-6">
          What dealers get <Arrow />
        </SmartLink>
      </div>
      <div className="pk-card overflow-hidden" data-reveal style={{ ['--reveal-delay' as string]: '0.08s' }}>
        <div className="grid border-b border-bm-line sm:grid-cols-2">
          <div className="p-6 sm:border-r sm:border-bm-line sm:p-8">
            <div className="text-[14px] text-bm-muted">Up to 100 active branches</div>
            <div className="mt-2 text-[34px] font-extrabold tracking-[-0.03em] text-bm-indigo">{partner.base[cur]}</div>
            <div className="text-[14px] text-bm-muted">a year, or {partner.base.monthly[cur]} a month</div>
          </div>
          <div className="border-t border-bm-line p-6 sm:border-t-0 sm:p-8">
            <div className="text-[14px] text-bm-muted">Each further 50 branches</div>
            <div className="mt-2 text-[34px] font-extrabold tracking-[-0.03em] text-bm-indigo">+{partner.extra[cur]}</div>
            <div className="text-[14px] text-bm-muted">a year</div>
          </div>
        </div>
        <div className="bg-bm-alt px-6 py-4 text-[14.5px] text-bm-ink sm:px-8">
          For example, 150 branches comes to <strong className="font-semibold text-bm-indigo">{partner.example150[cur]} a year</strong>, with unlimited dealer users.
        </div>
      </div>
    </div>
  );
}

function Allowances({ cur }: { cur: Cur }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
      <div data-reveal>
        <TwoTone lead="What is included." />
        <p className="mt-6 max-w-sm text-[16px] text-bm-muted">
          Cap spending on each AI meter; at the cap, that feature pauses until the next period. Tags are never capped, because they are printed on products.
        </p>
      </div>
      <div className="overflow-x-auto" data-reveal>
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-bm-indigo text-[13.5px] font-semibold text-bm-muted">
              <th className="py-3 pr-4 font-semibold">Included</th>
              <th className="px-4 py-3 font-semibold">Connect</th>
              <th className="py-3 pl-4 font-semibold text-bm-indigo">Scale</th>
            </tr>
          </thead>
          <tbody>
            {meters.map((m) => (
              <tr key={m.k} className="border-b border-bm-line align-top">
                <th scope="row" className="py-4 pr-4">
                  <div className="text-[16px] font-semibold text-bm-indigo">{m.k}</div>
                  <div className="text-[13.5px] font-normal text-bm-muted">{m.period}</div>
                </th>
                <td className="px-4 py-4">
                  <div className="text-[16px] font-semibold text-bm-ink">{m.connect}</div>
                  <div className="text-[13px] text-bm-muted">
                    then {m.then.connect[cur]} a {m.unit}
                  </div>
                </td>
                <td className="py-4 pl-4">
                  <div className="text-[16px] font-semibold text-bm-indigo">{m.scale}</div>
                  <div className="text-[13px] text-bm-muted">
                    then {m.then.scale[cur]} a {m.unit}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-5 text-[13.5px] text-bm-muted">
          A tag is one unique product identifier, such as a QR code for one unit. An AI resolution is one support conversation the AI answers. Usage packs at lower rates are on the price list.
        </p>
      </div>
    </div>
  );
}

function Leaving({ cur }: { cur: Cur }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
      <div data-reveal>
        <h2 className="t-h2">
          You own the data, the customers <Swipe>and the AI.</Swipe>
        </h2>
        <p className="mt-6 max-w-sm text-[16px] text-bm-muted">
          What you put in and what it learns stay yours. Here is every other line on the price list.
        </p>
        <SmartLink href="/why-trust-us" className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-bm-indigo hover:text-bm-emerald">
          What we put in the contract <Arrow />
        </SmartLink>
      </div>
      <dl className="border-t border-bm-line" data-reveal>
        {other.map((o) => (
          <div key={o.t} className="grid gap-1 border-b border-bm-line py-4 sm:grid-cols-[1fr_auto] sm:gap-8">
            <dt className="text-[15.5px] text-bm-ink">{o.t}</dt>
            <dd className={cx('text-[15.5px] font-semibold sm:text-right', o.v[cur] === 'Free' || o.v[cur].startsWith('Free') ? 'text-bm-emerald' : 'text-bm-indigo')}>{o.v[cur]}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function Pricing() {
  const [cur, setCur] = useState<Cur>('GBP');
  const plansRef = useReveal<HTMLElement>();
  usePageMeta(
    'Pricing for product registration, warranty and dealer software',
    'Connect £699 (€849) a month, month to month. Scale £2,500 (€3,000) a month. Dealer add-on priced by branch. No per-user fees, and you own your data.'
  );
  useJsonLd('bc-pricing', breadcrumbJsonLd([{ name: 'Home', path: '/' }, { name: 'Pricing', path: '/pricing' }]));

  return (
    <div className="pr">
      <KitStyles />
      <style>{`
.v2 .pr-price { font-weight: 800; color: var(--bm-indigo); font-size: clamp(52px, 5.6vw, 72px); line-height: 1; letter-spacing: -0.045em; font-variant-numeric: tabular-nums; }
.v2 .pr-price-sm { font-size: clamp(44px, 4.4vw, 56px); }
`}</style>

      <section className="pk-hero !pb-0">
        <div className="wrap">
          <div className="max-w-[46rem]">
            <h1 className="pk-h1">
              Start with <Swipe draw>one product line.</Swipe>
            </h1>
            <p className="t-lede mt-6">
              Pilot one line month to month, or take your whole range annually.
            </p>
          </div>
        </div>
      </section>

      <section ref={plansRef} className="pt-12 sm:pt-14">
        <div className="wrap">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[14px] text-bm-muted">Prices exclude VAT</span>
            <CurrencyToggle cur={cur} set={setCur} />
          </div>
          <Plans cur={cur} />
          <ul className="mt-10 grid gap-x-8 gap-y-3 border-y border-bm-line py-6 text-[15px] text-bm-ink sm:grid-cols-3">
            {['No per-user fees', 'You own your data', 'GDPR, DPA in the terms'].map((x) => (
              <li key={x} className="flex gap-2.5">
                <Check />
                {x}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[14px] text-bm-muted">
            From our binding Price List {PRICE_LIST_VERSION}, effective {PRICE_LIST_EFFECTIVE}. Your Order Form fixes the version that applies to you.{' '}
            <SmartLink href={cta.priceListHref} className="font-semibold text-bm-indigo underline decoration-bm-line underline-offset-4 hover:decoration-bm-indigo">
              Read the full price list
            </SmartLink>
          </p>
        </div>
      </section>

      <Section>
        <Dealers cur={cur} />
      </Section>

      <Section alt>
        <Allowances cur={cur} />
      </Section>

      <Section>
        <Leaving cur={cur} />
      </Section>

      <Section alt>
        <Faq faqs={pricingFaqs} id="faq-pricing" />
      </Section>

      <CtaBand source="pricing" />
    </div>
  );
}
